// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Pagination from "@stoneage/MobiusOverrides/Pagination/Pagination";
import * as React from "react";
import { useEffect, useState } from "react";
import { css } from "styled-components";

const enum fields {
    nodeId = "nodeId",
    overrideTitle = "overrideTitle",
    defaultPageSize = "defaultPageSize",
}

export interface Props extends WidgetProps {
    fields: {
        [fields.overrideTitle]: string;
        [fields.nodeId]: LinkFieldValue;
        [fields.defaultPageSize]: number;
    };
}

export interface ItemListStyles {
    itemListWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    linkWrapper?: InjectableCss;
}

export const itemListStyles: ItemListStyles = {
    itemListWrapper: {
        css: css`
            text-align: right;
            padding: 10px 0;
            text-transform: capitalize;
        `,
    },
    title: {
        css: css`
            color: ${getColor("common.ui1")};
        `,
    },
    titleWrapper: {
        css: css`
            font-weight: bold;
            margin-bottom: 10px;
            font-size: 17px;
        `,
    },
    linkWrapper: {
        css: css`
            text-align: right;
            padding: 5px 18px 0 0;
        `,
    },
};

export const ItemList = ({ fields: { overrideTitle, nodeId, defaultPageSize } }: Props) => {
    const rootPageLink = useAppSelector(state => getPageLinkByNodeId(state, nodeId?.value));

    const styles = useMergeStyles("itemList", itemListStyles);

    const [currentPage, setCurrentPage] = useState(1);
    const startingPageSize = defaultPageSize ?? 5;
    const [currentPageSize, setCurrentPageSize] = useState(startingPageSize);
    useEffect(() => {
        setCurrentPage(1);
        setCurrentPageSize(defaultPageSize ?? 5);
    }, [defaultPageSize]);

    const pageSizes = [startingPageSize, startingPageSize * 2, startingPageSize * 3, startingPageSize * 4];

    const filteredPages = rootPageLink?.children?.filter(o => !o.excludeFromNavigation);

    if (!rootPageLink || !filteredPages || filteredPages.length === 0) {
        return null;
    }

    return (
        <StyledWrapper css={styles.itemListWrapper}>
            <StyledWrapper {...styles.titleWrapper}>
                <Typography as="h3" {...styles.title}>
                    {overrideTitle || rootPageLink.title}
                </Typography>
            </StyledWrapper>
            {filteredPages.map((link, index) => {
                if (index < (currentPage - 1) * currentPageSize || index >= currentPage * currentPageSize) {
                    return;
                }

                return (
                    <StyledWrapper key={link.url} css={styles.linkWrapper}>
                        {link.url && <Link href={link.url}>{link.title}</Link>}
                    </StyledWrapper>
                );
            })}
            <Pagination
                {...styles.pagination}
                resultsCount={filteredPages.length}
                currentPage={currentPage}
                resultsPerPage={currentPageSize}
                resultsPerPageOptions={pageSizes}
                onChangePage={setCurrentPage}
                onChangeResultsPerPage={(event, pageSize) => setCurrentPageSize(pageSize)}
            />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ItemList,
    definition: {
        group: "Basic",
        icon: "link-simple",
        fieldDefinitions: [
            {
                name: fields.overrideTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.defaultPageSize,
                editorTemplate: "IntegerField",
                defaultValue: 5,
                fieldType: "General",
            },
            {
                name: fields.nodeId,
                displayName: "Page",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                isRequired: true,
                fieldType: "General",
                allowUrls: () => false,
                allowCategories: () => false,
            },
        ],
    },
};

export default widgetModule;
